/**
 * Settings for the silent-Anna sweep: node --test
 *
 * The server (firebase/functions/silentOnlineSweep.js) switches an Anna offline when he is marked
 * online but his phone has been silent for a long time. It reads two top-level fields of
 * config/platform on every run (every 15 minutes), with no cache:
 *
 *   silentOfflineMinutes  missing/blank/unreadable = 60; 0 or less = the sweep is OFF;
 *                         otherwise clamped to 15..720.
 *   silentOfflinePush     false (or the text "false") = he is not sent the "you were taken
 *                         offline" notification; anything else = he is.
 *
 * These helpers read those fields the same way the server does (so the box always shows what the
 * server will really do) and build the payload the box saves. The payload holds ONLY these two
 * fields, and they are top-level, never inside `dispatch`: saving Dispatch tuning replaces that
 * whole block and would wipe them. A test keeps the constants and field names equal to the server's.
 */

export const SILENT_MIN_MINUTES = 15;
export const SILENT_MAX_MINUTES = 720;
export const SILENT_DEFAULT_MINUTES = 60;

/**
 * What the server will do for this config/platform document: { enabled, minutes, pushEnabled }.
 * `minutes` is what the box shows; when the sweep is off it is the default, ready for turning it on.
 */
export function readSilentSweepSettings(cfg) {
  const raw = cfg ? cfg.silentOfflineMinutes : undefined;
  let enabled = true;
  let minutes = SILENT_DEFAULT_MINUTES;
  if (!(raw === undefined || raw === null || raw === '')) {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      if (n <= 0) enabled = false;
      else minutes = Math.round(Math.min(Math.max(n, SILENT_MIN_MINUTES), SILENT_MAX_MINUTES));
    }
  }
  const push = cfg ? cfg.silentOfflinePush : undefined;
  const muted = push === false || (typeof push === 'string' && push.trim().toLowerCase() === 'false');
  return { enabled, minutes, pushEnabled: !muted };
}

/** A whole number of minutes from 15 to 720, or null. */
export function parseSilentMinutes(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < SILENT_MIN_MINUTES || n > SILENT_MAX_MINUTES) return null;
  return n;
}

/**
 * The payload to save, or an error to show. Turning the sweep off saves 0 (the server's "off"),
 * whatever is typed in the minutes box.
 */
export function buildSilentSweepPayload({ enabled, minutesText, pushEnabled }) {
  const silentOfflinePush = Boolean(pushEnabled);
  if (!enabled) return { ok: true, payload: { silentOfflineMinutes: 0, silentOfflinePush } };
  const minutes = parseSilentMinutes(minutesText);
  if (minutes === null) {
    return { ok: false, error: `Enter a whole number of minutes from ${SILENT_MIN_MINUTES} to ${SILENT_MAX_MINUTES}` };
  }
  return { ok: true, payload: { silentOfflineMinutes: minutes, silentOfflinePush } };
}

/**
 * True when this save can switch off Annas that stay online today: turning the sweep on, or
 * shortening the time. The box asks the admin to confirm first.
 */
export function needsConfirm(saved, payload) {
  const next = payload ? payload.silentOfflineMinutes : 0;
  return next > 0 && (!saved || !saved.enabled || next < saved.minutes);
}
